import React, { useEffect } from 'react';

interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  /** 
   * If true, pressing Enter anywhere in the form will trigger this button's click event
   * Only one button in a form should have this property set to true
   */
  isDefaultSubmit?: boolean;
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  fullWidth = true,
  className = '',
  isDefaultSubmit = false,
  onClick,
  type = 'button',
  ...props
}) => {
  // Handle Enter key press to trigger the button click
  useEffect(() => {
    if (!isDefaultSubmit) return;
    
    const handleEnterKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && onClick && !event.isComposing) {
        // Only trigger if the active element is not a button or textarea
        const activeElement = document.activeElement;
        const tagName = activeElement?.tagName.toLowerCase();
        
        // Don't trigger if we're in a textarea or the event happened on a button
        if (tagName === 'textarea' || tagName === 'button') {
          return;
        }
        
        event.preventDefault();
        onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
      }
    };

    document.addEventListener('keydown', handleEnterKeyPress);
    return () => {
      document.removeEventListener('keydown', handleEnterKeyPress);
    };
  }, [isDefaultSubmit, onClick]);

  return (
    <button
      {...props}
      type={type}
      onClick={onClick}
      className={`h-[50px] bg-[#5A4099] text-white font-bold rounded-lg hover:opacity-90 transition-opacity ${
        fullWidth ? 'w-full' : ''
      } ${className}`}
    >
      {children}
    </button>
  );
};

export default PrimaryButton;
